// ConvertTypeModal.js — generic "Change Type" dialog driven by utils/conversions.js.
// Pick a target type in the family × dims matrix (or a computed type chip),
// tweak the conversion's options, check the live preview, then Apply.
import React, { useState, useMemo, useEffect } from "react";
import {
  FAMILIES, DIMS, COMPUTED_TYPES, typeName, typeMeta,
  varCountStatus, getConversion, AXES_BY_DIMS,
} from "../utils/conversions";
import "./styles/ConvertTypeModal.css";

const DIM_LABELS = { 1: "1D (flat)", 2: "2D (rows)", 3: "3D (rows×cols)", 4: "4D (+statements)" };

const ConvertTypeModal = ({ isOpen, onClose, selectedKey, variable, dataGroup, onApply }) => {
  const [targetType, setTargetType] = useState(null);
  const [params, setParams] = useState({});

  const fromType = variable?.type;
  const fromMeta = typeMeta(fromType);
  const vc = varCountStatus(variable || {});

  const conversion = useMemo(
    () => (targetType ? getConversion(variable, selectedKey, targetType) : null),
    [variable, selectedKey, targetType]
  );

  // Reset params to the conversion's defaults whenever the target changes.
  useEffect(() => {
    if (conversion?.status === "ok") {
      const defaults = {};
      (conversion.params || []).forEach((p) => { defaults[p.name] = p.default; });
      setParams(defaults);
    }
  }, [targetType]); // eslint-disable-line react-hooks/exhaustive-deps

  const validation = useMemo(() => {
    if (conversion?.status !== "ok") return null;
    try { return conversion.validate(variable, params); }
    catch (e) { return { ok: false, errors: [String(e.message || e)] }; }
  }, [conversion, variable, params]);

  const preview = useMemo(() => {
    if (conversion?.status !== "ok" || !validation?.ok) return null;
    try { return conversion.apply(variable, selectedKey, params, dataGroup); }
    catch (e) { return { error: String(e.message || e) }; }
  }, [conversion, validation, variable, selectedKey, params, dataGroup]);

  if (!isOpen || !variable) return null;

  const cellFor = (type) => (type ? getConversion(variable, selectedKey, type) : { status: "disabled", reason: "Type doesn't exist." });

  const renderParam = (spec) => {
    const val = params[spec.name] !== undefined ? params[spec.name] : spec.default;
    switch (spec.type) {
      case "checkbox":
        return (
          <label key={spec.name} className="ctm-param ctm-param--check">
            <input type="checkbox" checked={!!val}
              onChange={(e) => setParams((p) => ({ ...p, [spec.name]: e.target.checked }))} />
            <span>{spec.label}</span>
          </label>
        );
      case "select":
        return (
          <div key={spec.name} className="ctm-param">
            <label className="ctm-param-label">{spec.label}</label>
            <select className="ctm-param-input" value={val}
              onChange={(e) => setParams((p) => ({ ...p, [spec.name]: e.target.value }))}>
              {(spec.choices || []).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
        );
      case "textarea":
        return (
          <div key={spec.name} className="ctm-param ctm-param--wide">
            <label className="ctm-param-label">{spec.label}</label>
            <textarea className="ctm-param-input ctm-param-textarea" value={val || ""}
              placeholder={spec.placeholder || ""}
              onChange={(e) => setParams((p) => ({ ...p, [spec.name]: e.target.value }))} />
            {spec.help && <span className="ctm-param-help">{spec.help}</span>}
          </div>
        );
      default:
        return (
          <div key={spec.name} className="ctm-param">
            <label className="ctm-param-label">{spec.label}</label>
            <input type="text" className="ctm-param-input" value={val || ""}
              placeholder={spec.placeholder || ""}
              onChange={(e) => setParams((p) => ({ ...p, [spec.name]: e.target.value }))} />
            {spec.help && <span className="ctm-param-help">{spec.help}</span>}
          </div>
        );
    }
  };

  const previewEntries = preview && !preview.error ? Object.entries(preview.add) : [];
  const canApply = conversion?.status === "ok" && validation?.ok && preview && !preview.error;

  return (
    <div className="ctm-overlay" onClick={onClose}>
      <div className="ctm-modal" onClick={(e) => e.stopPropagation()}>

        <div className="ctm-header">
          <div>
            <h3 className="ctm-title">Change Type — {selectedKey}</h3>
            <div className="ctm-current">
              Current: <span className="ctm-type-badge">{fromType || "?"}</span>
              {vc.expected !== null && (
                <span className={`ctm-vc ${vc.ok ? "ctm-vc--ok" : "ctm-vc--bad"}`}>
                  {vc.ok ? "✓" : "⚠"} {vc.actual} varName{vc.actual !== 1 ? "s" : ""}
                  {" "}(expected {vc.expected}
                  {fromMeta && !fromMeta.computed && fromMeta.dims > 1 &&
                    ` = ${AXES_BY_DIMS[fromMeta.dims].map((a) => (variable[a] || []).length).join(" × ")}`})
                </span>
              )}
            </div>
          </div>
          <button className="ctm-close" onClick={onClose}>✕</button>
        </div>

        {/* ── target matrix ── */}
        <div className="ctm-matrix">
          <div className="ctm-matrix-row ctm-matrix-head">
            <div className="ctm-matrix-corner" />
            {DIMS.map((d) => <div key={d} className="ctm-matrix-colhead">{DIM_LABELS[d]}</div>)}
          </div>
          {FAMILIES.map((family) => (
            <div key={family} className="ctm-matrix-row">
              <div className="ctm-matrix-rowhead">{family}</div>
              {DIMS.map((dims) => {
                const t = typeName(family, dims);
                const res = cellFor(t);
                const cls =
                  res.status === "current" ? "ctm-cell--current" :
                  res.status === "ok" ? (t === targetType ? "ctm-cell--selected" : "ctm-cell--ok") :
                  "ctm-cell--disabled";
                return (
                  <button key={dims}
                    className={`ctm-cell ${cls}`}
                    title={res.status === "disabled" ? res.reason : t || ""}
                    disabled={res.status !== "ok"}
                    onClick={() => setTargetType(t)}>
                    {t ? (res.status === "current" ? "now" : t.replace(family, "")) || t : "—"}
                  </button>
                );
              })}
            </div>
          ))}
          <div className="ctm-computed-row">
            {COMPUTED_TYPES.map((t) => {
              const res = cellFor(t);
              const cls =
                res.status === "current" ? "ctm-cell--current" :
                res.status === "ok" ? (t === targetType ? "ctm-cell--selected" : "ctm-cell--ok") :
                "ctm-cell--disabled";
              return (
                <button key={t}
                  className={`ctm-cell ctm-cell--computed ${cls}`}
                  title={res.status === "disabled" ? res.reason : t}
                  disabled={res.status !== "ok"}
                  onClick={() => setTargetType(t)}>
                  {res.status === "current" ? `${t} (now)` : t}
                </button>
              );
            })}
          </div>
          <div className="ctm-hint">Grey targets are disabled — hover them to see why.</div>
        </div>

        {/* ── options + preview ── */}
        {conversion?.status === "ok" && (
          <>
            <div className="ctm-section">
              <div className="ctm-section-title">{fromType} → {targetType}</div>
              <div className="ctm-conv-label">{conversion.label}</div>
              {(conversion.params || []).length > 0 && (
                <div className="ctm-params">{conversion.params.map(renderParam)}</div>
              )}
            </div>

            {validation && !validation.ok && (
              <div className="ctm-errors">
                {validation.errors.map((e, i) => <div key={i} className="ctm-error">⚠ {e}</div>)}
              </div>
            )}

            {preview && !preview.error && (
              <div className="ctm-section">
                <div className="ctm-section-title">
                  Preview — {previewEntries.length} question{previewEntries.length !== 1 ? "s" : ""}
                  {preview.remove.length > 0 && `, removes ${preview.remove.join(", ")}`}
                </div>
                {(preview.warnings || []).map((w, i) => <div key={i} className="ctm-warning">⚠ {w}</div>)}
                <div className="ctm-preview-list">
                  {previewEntries.slice(0, 12).map(([k, v]) => (
                    <div key={k} className="ctm-preview-item">
                      <span className="ctm-preview-key">{k}</span>
                      <span className="ctm-type-badge ctm-type-badge--sm">{v.type}</span>
                      <span className="ctm-preview-detail">
                        {(v.varName || []).length > 0 && `${v.varName.length} var${v.varName.length !== 1 ? "s" : ""}`}
                        {(v.Rows || []).length > 0 && ` · ${v.Rows.length} rows`}
                        {(v.Cols || []).length > 0 && ` × ${v.Cols.length} cols`}
                        {(v.values || []).length > 0 && ` · ${v.values.length} values`}
                        {(v.stackedRows || []).length > 0 && ` · ${v.stackedRows.length} stacked rows`}
                        {(v.volumeRows || []).length > 0 && ` · ${v.volumeRows.length} volume rows`}
                        {(v.shareRows || []).length > 0 && ` · ${v.shareRows.length} share rows`}
                        {(v.customRows || []).length > 0 && ` · ${v.customRows.length} custom rows`}
                      </span>
                      <span className="ctm-preview-title" title={v.Qtitle}>{v.Qtitle}</span>
                    </div>
                  ))}
                  {previewEntries.length > 12 && (
                    <div className="ctm-preview-more">…and {previewEntries.length - 12} more</div>
                  )}
                </div>
              </div>
            )}
            {preview?.error && <div className="ctm-errors"><div className="ctm-error">⚠ {preview.error}</div></div>}
          </>
        )}

        <div className="ctm-footer">
          <button className="ctm-btn" onClick={onClose}>Cancel</button>
          <button
            className="ctm-btn ctm-btn--primary"
            disabled={!canApply}
            onClick={() => { onApply(preview, `${fromType} → ${targetType}`); onClose(); }}>
            Apply Conversion
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConvertTypeModal;
